import { signal } from '@preact/signals-core';
import {
  pagePartElementsMap,
  pageStructureMap,
} from '@streamlain/database-identity';
import { Subject } from 'rxjs';
import * as Y from 'yjs';

import type { Store } from '../../model';
import { StoreExtension } from '../store-extension';

/**
 * Origin of the changes `Store.transactYjs` writes straight to the Yjs
 * document: undo tracks them like model changes, and `force` makes the models
 * take them up although they are local (see `BaseReactiveYData`).
 */
export const YJS_WRITE_ORIGIN = Object.freeze({ force: true });

/**
 * The undo managers one user step touched, one per document, with the number
 * of their stack items the step holds.
 */
type HistoryStep = Map<Y.UndoManager, number>;

/**
 * Writes `write` into the step that was newest when the hold was taken, as
 * long as no other step followed it; otherwise `write` opens a step of its
 * own.
 */
export type HistoryStepHold = <T>(write: () => T) => T;

/**
 * History of a page over its documents: one Y.UndoManager per document (the
 * page document with the structure and part 0, and every bound part), and
 * one stack of user steps above them. A step is what one user action wrote:
 * it ends at `captureSync` or with the task that wrote it; undo and redo take
 * a whole step back over every document it touched.
 */
export class HistoryExtension extends StoreExtension {
  static override readonly key = 'history';

  private readonly _history!: Y.UndoManager;

  private readonly _parts = new Map<Y.Doc, Y.UndoManager>();

  private _undoSteps: HistoryStep[] = [];

  private _redoSteps: HistoryStep[] = [];

  private _openStep: HistoryStep | null = null;

  // The held step a `HistoryStepHold` is writing into.
  private _joining: HistoryStep | null = null;

  private _replaying = false;

  private readonly _listeners = new Map<
    Y.UndoManager,
    {
      added: (event: { type: 'undo' | 'redo' }) => void;
      cleared: () => void;
    }
  >();

  private readonly _canRedo = signal(false);

  private readonly _canUndo = signal(false);

  readonly onUpdated = new Subject<void>();

  constructor(store: Store) {
    super(store);

    // Moves and deletes write the page structure along with the fields;
    // canvas elements keep their fields in a map of their own.
    this._history = new Y.UndoManager(
      [
        this.store.doc.yBlocks,
        pageStructureMap(this.store.doc.spaceDoc),
        pagePartElementsMap(this.store.doc.spaceDoc),
      ],
      {
        trackedOrigins: new Set<unknown>([
          this.store.doc.spaceDoc.clientID,
          YJS_WRITE_ORIGIN,
        ]),
      }
    );
    // Steps are counted from the first change on, also before the store
    // has loaded.
    this._observe(this._history);
  }

  private get _managers(): Y.UndoManager[] {
    return [this._history, ...this._parts.values()];
  }

  private readonly _updateCanUndoRedoSignals = () => {
    const canRedo = this._redoSteps.length > 0;
    const canUndo = this._undoSteps.length > 0;
    if (this._canRedo.peek() !== canRedo) {
      this._canRedo.value = canRedo;
    }
    if (this._canUndo.peek() !== canUndo) {
      this._canUndo.value = canUndo;
    }
  };

  get canRedo() {
    return this._canRedo.peek();
  }

  /** Undo or redo is applying a step: what changes meanwhile is restored, not made. */
  get replaying() {
    return this._replaying;
  }

  get canUndo() {
    return this._canUndo.peek();
  }

  get canRedo$() {
    return this._canRedo;
  }

  get canUndo$() {
    return this._canUndo;
  }

  /** Undo manager of the page document. */
  get undoManager() {
    return this._history;
  }

  /** Undo manager of the document a Yjs type lives in. */
  undoManagerOf(doc: Y.Doc | null): Y.UndoManager {
    return (doc && this._parts.get(doc)) ?? this._history;
  }

  /** Tracks a loaded part document: its types join the steps of the page. */
  bindPart(
    doc: Y.Doc,
    types: ConstructorParameters<typeof Y.UndoManager>[0]
  ): void {
    if (this._parts.has(doc) || doc === this.store.doc.spaceDoc) return;
    const manager = new Y.UndoManager(
      [...(Array.isArray(types) ? types : [types]), pageStructureMap(doc)],
      {
        // Writes of the store carry the part's client id; a write without an
        // origin, such as a sync, is no step of this device.
        trackedOrigins: new Set<unknown>([doc.clientID, YJS_WRITE_ORIGIN]),
      }
    );
    this._parts.set(doc, manager);
    this._observe(manager);
  }

  /** An unloaded part leaves the history; steps of it alone go. */
  unbindPart(doc: Y.Doc): void {
    const manager = this._parts.get(doc);
    if (!manager) return;
    this._parts.delete(doc);
    this._unobserve(manager);
    this._forget(manager);
    manager.destroy();
    this._historyObserver();
  }

  /**
   * A hold on the newest step, for what one action writes later, such as a
   * block its owner inserts after a request: one undo takes it back with
   * the rest of the action.
   */
  holdStep(): HistoryStepHold {
    const step = this._undoSteps.at(-1);
    return write => {
      if (!step || this._undoSteps.at(-1) !== step) return write();
      this._joining = step;
      try {
        return write();
      } finally {
        this._joining = null;
      }
    };
  }

  /** Ends the current step: the next change starts a new one. */
  stopCapturing(): void {
    this._openStep = null;
    this._managers.forEach(manager => manager.stopCapturing());
  }

  undo(): void {
    this._replay(this._undoSteps, this._redoSteps, manager => manager.undo());
  }

  redo(): void {
    this._replay(this._redoSteps, this._undoSteps, manager => manager.redo());
  }

  clear(): void {
    this._openStep = null;
    this._replaying = true;
    this._managers.forEach(manager => manager.clear());
    this._replaying = false;
    this._undoSteps = [];
    this._redoSteps = [];
    this._historyObserver();
  }

  private _replay(
    from: HistoryStep[],
    to: HistoryStep[],
    apply: (manager: Y.UndoManager) => void
  ): void {
    this.stopCapturing();
    const step = from.pop();
    if (!step) return;
    this._replaying = true;
    try {
      this.store.pageStructure.batchChanges(() => {
        step.forEach((items, manager) => {
          for (let item = 0; item < items; item++) apply(manager);
        });
      });
    } finally {
      this._replaying = false;
    }
    to.push(step);
    this._historyObserver();
  }

  // A user change of one document: it joins the open step or opens one,
  // which ends the capture of every other document and drops the redo steps.
  private _added(manager: Y.UndoManager, type: 'undo' | 'redo'): void {
    if (this._replaying || type !== 'undo') return;
    if (this._joining) {
      this._joining.set(manager, (this._joining.get(manager) ?? 0) + 1);
      return;
    }
    if (!this._openStep || this._openStep.has(manager)) {
      const step: HistoryStep = new Map();
      this._openStep = step;
      this._undoSteps.push(step);
      this._managers.forEach(other => {
        if (other !== manager) other.stopCapturing();
      });
      if (this._redoSteps.length > 0) {
        this._redoSteps = [];
        this._managers.forEach(other => {
          if (other !== manager) other.clear(false, true);
        });
      }
      queueMicrotask(() => {
        if (this._openStep === step) this._openStep = null;
      });
    }
    this._openStep.set(manager, 1);
  }

  private _forget(manager: Y.UndoManager): void {
    const keep = (steps: HistoryStep[]) =>
      steps.filter(step => {
        step.delete(manager);
        return step.size > 0;
      });
    this._undoSteps = keep(this._undoSteps);
    this._redoSteps = keep(this._redoSteps);
  }

  private _observe(manager: Y.UndoManager): void {
    const listeners = {
      added: (event: { type: 'undo' | 'redo' }) => {
        this._added(manager, event.type);
        this._historyObserver();
      },
      cleared: () => {
        if (this._replaying) return;
        this._forget(manager);
        this._historyObserver();
      },
    };
    this._listeners.set(manager, listeners);
    manager.on('stack-item-added', listeners.added);
    manager.on('stack-cleared', listeners.cleared);
    manager.on('stack-item-popped', this._historyObserver);
    manager.on('stack-item-updated', this._historyObserver);
  }

  private _unobserve(manager: Y.UndoManager): void {
    const listeners = this._listeners.get(manager);
    if (!listeners) return;
    this._listeners.delete(manager);
    manager.off('stack-item-added', listeners.added);
    manager.off('stack-cleared', listeners.cleared);
    manager.off('stack-item-popped', this._historyObserver);
    manager.off('stack-item-updated', this._historyObserver);
  }

  override loaded() {
    this._updateCanUndoRedoSignals();
  }

  private readonly _historyObserver = () => {
    this._updateCanUndoRedoSignals();
    this.onUpdated.next();
  };

  override disposed() {
    super.disposed();
    this._managers.forEach(manager => this._unobserve(manager));
    this._parts.forEach(manager => manager.destroy());
    this._parts.clear();
    this.onUpdated.complete();
  }
}
